import {
  NAME_EDITION,
  NAME_FIRST_YEAR,
  NAME_LAST_YEAR,
  NAME_SOURCE,
  type NameSummary,
} from "@listou/shared/schemas/prenoms";
import { Link, useNavigate } from "@tanstack/react-router";
import { Baby, CalendarDays, MapPin, Search, TrendingUp, type LucideIcon } from "lucide-react";
import { useState, type ComponentProps, type ReactNode } from "react";

import { SiteFooter, SiteHeader } from "../site-header";
import { normalize, number } from "./format";

export const card = "rounded-3xl border border-line bg-surface p-6 sm:p-8";
export const pill =
  "inline-flex items-center gap-1.5 rounded-full border border-line bg-surface px-4 py-2 text-sm text-ink transition hover:border-ink";

export function NamesLayout({ children }: { children: ReactNode }) {
  return (
    <div className="min-h-dvh">
      <SiteHeader signedIn={false} />
      <main className="mx-auto max-w-6xl space-y-10 px-4 py-8 sm:px-6 sm:py-10">{children}</main>
      <SiteFooter />
    </div>
  );
}

const TABS: {
  to: "/prenoms" | "/prenoms/annee" | "/prenoms/departement" | "/prenoms/tendances";
  label: string;
  icon: LucideIcon;
}[] = [
  { to: "/prenoms", label: "Tous les prénoms", icon: Baby },
  { to: "/prenoms/annee", label: "Par année", icon: CalendarDays },
  { to: "/prenoms/departement", label: "Par département", icon: MapPin },
  { to: "/prenoms/tendances", label: "Tendances", icon: TrendingUp },
];
export function PrenomsNav({
  current,
  share,
}: {
  current: (typeof TABS)[number]["to"];
  /** The page's share button, kept on the tabs' row. */
  share?: ReactNode;
}) {
  return (
    <div className="flex flex-wrap items-center justify-between gap-3">
      <nav aria-label="Atlas des prénoms" className="flex flex-wrap gap-2">
        {TABS.map((tab) => (
          <Link
            key={tab.to}
            to={tab.to}
            aria-current={current === tab.to ? "page" : undefined}
            className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-bold ${current === tab.to ? "bg-ink text-white" : "border border-line hover:border-ink"}`}
          >
            <tab.icon size={16} aria-hidden="true" />
            {tab.label}
          </Link>
        ))}
      </nav>
      {share}
    </div>
  );
}

export type Crumb = { label: string; to?: string };
export function Breadcrumbs({ items }: { items: Crumb[] }) {
  return (
    <nav aria-label="Fil d’Ariane" className="text-sm text-muted">
      <ol className="flex flex-wrap items-center gap-x-2">
        {items.map((item, index) => (
          <li key={item.label} className="flex items-center gap-2">
            {item.to ? (
              <Link to={item.to} className="underline">
                {item.label}
              </Link>
            ) : (
              <span aria-current="page">{item.label}</span>
            )}
            {index < items.length - 1 && <span aria-hidden="true">/</span>}
          </li>
        ))}
      </ol>
    </nav>
  );
}

export function NameLink({
  slug,
  children,
  year,
  ...rest
}: {
  slug: string;
  children: ReactNode;
  year?: number;
} & Omit<ComponentProps<"a">, "href" | "children">) {
  return (
    <Link to="/prenoms/$slug" params={{ slug }} search={year ? { annee: year } : {}} {...rest}>
      {children}
    </Link>
  );
}
export function YearLink({
  year,
  children,
  className,
}: {
  year: number;
  children?: ReactNode;
  className?: string;
}) {
  return (
    <Link to="/prenoms/annee/$year" params={{ year: String(year) }} className={className}>
      {children ?? year}
    </Link>
  );
}
export function DepartmentLink({
  slug,
  children,
  className,
}: {
  slug: string;
  children: ReactNode;
  className?: string;
}) {
  return (
    <Link to="/prenoms/departement/$department" params={{ department: slug }} className={className}>
      {children}
    </Link>
  );
}
export function YearDepartmentLink({
  year,
  slug,
  children,
  className,
}: {
  year: number;
  slug: string;
  children: ReactNode;
  className?: string;
}) {
  return (
    <Link
      to="/prenoms/annee/$year/$department"
      params={{ year: String(year), department: slug }}
      className={className}
    >
      {children}
    </Link>
  );
}

/**
 * Name search with an optional birth year. The year rides in the URL (`?annee=`), so the
 * personalised page is a plain link a crawler or a friend can open.
 */
export function NameFinder({ names, large = false }: { names: NameSummary[]; large?: boolean }) {
  const [query, setQuery] = useState("");
  const [year, setYear] = useState("");
  const navigate = useNavigate();
  const needle = normalize(query.trim());
  const matches =
    needle.length === 0
      ? []
      : names
          .filter((name) => normalize(name.name).includes(needle))
          .toSorted(
            (a, b) =>
              Number(normalize(b.name).startsWith(needle)) -
                Number(normalize(a.name).startsWith(needle)) || b.total - a.total,
          )
          .slice(0, 6);
  const birthYear = Number(year);
  const validYear =
    Number.isInteger(birthYear) && birthYear >= NAME_FIRST_YEAR && birthYear <= NAME_LAST_YEAR
      ? birthYear
      : undefined;
  return (
    <form
      role="search"
      onSubmit={(event) => {
        event.preventDefault();
        const exact = names.find((name) => normalize(name.name) === needle) ?? matches[0];
        if (exact) {
          void navigate({
            to: "/prenoms/$slug",
            params: { slug: exact.slug },
            search: validYear ? { annee: validYear } : {},
          });
        }
      }}
      className="space-y-3"
    >
      <div className="flex flex-col gap-3 sm:flex-row">
        <label className="flex-1">
          <span className="sr-only">Votre prénom</span>
          <input
            type="search"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Votre prénom : Louise, Gabriel, Camille…"
            autoComplete="off"
            className={`w-full rounded-2xl border-2 border-line bg-white px-5 text-ink ${large ? "py-4 text-xl" : "py-3 text-lg"}`}
          />
        </label>
        <label className="sm:w-64">
          <span className="sr-only">Année de naissance, facultative</span>
          <input
            type="number"
            inputMode="numeric"
            min={NAME_FIRST_YEAR}
            max={NAME_LAST_YEAR}
            value={year}
            onChange={(event) => setYear(event.target.value)}
            placeholder="Année de naissance"
            className={`w-full rounded-2xl border-2 border-line bg-white px-5 text-ink ${large ? "py-4 text-xl" : "py-3 text-lg"}`}
          />
        </label>
        <button type="submit" className="btn gap-2">
          <Search size={18} aria-hidden="true" />
          Voir mes stats
        </button>
      </div>
      {matches.length > 0 && (
        <ul className="flex flex-wrap gap-2" aria-label="Prénoms correspondants">
          {matches.map((name) => (
            <li key={name.slug}>
              <NameLink slug={name.slug} year={validYear} className={pill}>
                <strong>{name.name}</strong>{" "}
                <span className="text-muted">{number(name.total)} naissances</span>
              </NameLink>
            </li>
          ))}
        </ul>
      )}
      {needle.length > 0 && matches.length === 0 && (
        <p className="text-sm text-muted">
          Aucun prénom trouvé. Essayez une autre orthographe : les prénoms de moins de 100
          naissances ne sont pas publiés.
        </p>
      )}
    </form>
  );
}

export function Source() {
  return (
    <section className={`${card} space-y-3 text-sm text-muted`}>
      <h2 className="text-xl font-bold text-ink">Les chiffres, avec leurs limites</h2>
      <p>
        Source :{" "}
        <a href={NAME_SOURCE} className="underline" rel="noopener">
          Insee, fichier des prénoms, édition {NAME_EDITION}
        </a>
        . Premier prénom déclaré à l’état civil, naissances en France de {NAME_FIRST_YEAR} à{" "}
        {NAME_LAST_YEAR}. Les effectifs sont arrondis au multiple de 5 le plus proche, les
        classements sont calculés par l’Insee sur les valeurs exactes. Ces chiffres ne représentent
        pas les personnes encore vivantes.
      </p>
      <p>
        Les effectifs trop faibles sont masqués par l’Insee, au niveau national comme dans chaque
        département : une absence ne prouve pas qu’aucun enfant n’a reçu ce prénom, et la somme des
        prénoms publiés reste inférieure au nombre réel de naissances. Les données anciennes,
        surtout avant 1946, sont moins complètes. Les départements sont ceux d’aujourd’hui, y
        compris pour les naissances anciennes. Mayotte est incluse depuis 2013.
      </p>
      <p>
        Les cartes portent sur le département de naissance, pas sur le lieu de résidence. Les
        contours métropolitains proviennent de{" "}
        <a href="https://github.com/gregoiredavid/france-geojson" className="underline">
          france-geojson
        </a>{" "}
        (IGN Admin Express 2018, licence ouverte Etalab). Les départements d’outre-mer figurent dans
        les listes.
      </p>
    </section>
  );
}

export function Faq({ items }: { items: { question: string; answer: string }[] }) {
  return (
    <section className={`${card} space-y-4`}>
      <h2 className="text-2xl font-bold">Questions fréquentes</h2>
      <div className="divide-y divide-line">
        {items.map((item) => (
          <details key={item.question} className="group py-3">
            <summary className="cursor-pointer font-bold">{item.question}</summary>
            <p className="mt-2 text-muted">{item.answer}</p>
          </details>
        ))}
      </div>
    </section>
  );
}
